/*
 * Cart drawer
 * ---------------------------------------------------------------------------
 * Two things theme.js leaves undone for a drawer cart.
 *
 * 1. Escape. theme.js gives the mini cart everything else a drawer needs, but
 *    it binds no key handler, so there is no way to dismiss it from the
 *    keyboard. That is a dead end rather than an inconvenience: the theme calls
 *    Accessibility.trapFocus when the cart opens, so focus cannot leave the
 *    panel either.
 *
 * 2. The add buttons in snippets/cart-addon-cards.liquid. Every other
 *    add-to-cart in the theme is a section's own delegate on
 *    [data-action="add-to-cart"], bound to that section's element -- the
 *    collection, search and product sections each bind their own. The header,
 *    which owns the mini cart, binds the quantity controls and cart:refresh on
 *    document.documentElement but never the add action, so a button inside the
 *    drawer reaches no handler at all.
 *
 * Neither one changes the cart itself. Escape dispatches a click on the theme's
 * own close control rather than setting aria-hidden here, because hiding the
 * panel directly would leave theme.js believing it was still open, stranding its
 * internal flag, the focus trap and the scroll-lock class on <body>. The add
 * handler posts to /cart/add.js and then dispatches the theme's own cart:refresh,
 * so the count, the ladder, the recap and the add-on list itself all restate
 * through the path they already use.
 *
 * What filled the add-on list is not here. assets/cart-recommendations.js
 * replaces its cards with what Shopify recommends for the cart, and does the same
 * for the cart page's own cross-sell -- one script, because the only thing the
 * two surfaces do not share is the card.
 */
(function () {
  'use strict';

  var DRAWER = '#mini-cart';
  var CLOSERS = '.cart-drawer__close, [data-action="toggle-mini-cart"]';
  var ADD_BUTTON = '[data-cart-addon-add]';
  var ADDON_LIST = '.cart-addons__list';

  /* -------------------------------------------------------------- Escape ---- */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') {
      return;
    }

    var drawer = document.querySelector(DRAWER);

    if (!drawer || drawer.getAttribute('aria-hidden') !== 'false') {
      return;
    }

    // The close button lives inside the drawer; the cart icon in the header is
    // the fallback when the drawer is rendered without one.
    var closer = drawer.querySelector('.cart-drawer__close') || document.querySelector(CLOSERS);

    if (closer) {
      event.preventDefault();
      closer.click();
    }
  });

  /* ------------------------------------------------------- Add-on adding ---- */
  // Delegated on the document because the drawer's contents are replaced
  // wholesale on every cart change: a listener bound to a button would be
  // thrown away with the markup it was bound to.
  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest(ADD_BUTTON) : null;

    if (!button || button.hasAttribute('disabled')) {
      return;
    }

    var variantId = button.getAttribute('data-variant-id');

    if (!variantId) {
      return;
    }

    // The button sits inside the mini cart's own <form>, which posts to /cart.
    // It is type="button" so it cannot submit that form, but preventing the
    // default here as well costs nothing and covers a markup change later.
    event.preventDefault();

    // Disabled for the round trip so a double tap cannot add twice; the markup
    // it lives in is replaced on success, so it is only re-enabled on failure.
    button.setAttribute('disabled', 'disabled');
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    var addUrl = (window.routes && window.routes.cartAddUrl) || '/cart/add';

    fetch(addUrl + '.js', {
      body: JSON.stringify({ id: variantId, quantity: 1 }),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(function (response) {
        if (!response.ok) {
          // Sold out between render and tap is the likely case, and the next
          // re-render drops the product from the list anyway. Matching the
          // theme's other quick-add handlers, the button simply comes back.
          button.removeAttribute('disabled');
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
          return;
        }

        // theme.js listens for this on document.documentElement and re-fetches
        // ?view=mini-cart. It ends the loading state itself, so nothing else to
        // do here. scrollToTop only matters on the cart page, where this file
        // is not the one adding anything, but it is passed for correctness.
        button.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true, detail: { scrollToTop: false } })
        );
      })
      .catch(function () {
        button.removeAttribute('disabled');
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
      });
  });

  /* --------------------------------------------- Keeping the strip in place ---- */
  // theme.js restores the line item list's scrollTop across a re-render but
  // knows nothing about the add-on strip, so adding the third card snapped the
  // row back to the first. Remembered on scroll and put back on the theme's own
  // cart:rerendered, which is dispatched on the header section and does not
  // bubble -- a capture listener on the document still sees it on the way down.
  var listScrollLeft = 0;

  document.addEventListener(
    'scroll',
    function (event) {
      var target = event.target;

      if (target && target.classList && target.classList.contains('cart-addons__list')) {
        listScrollLeft = target.scrollLeft;
      }
    },
    true
  );

  document.addEventListener(
    'cart:rerendered',
    function () {
      if (!listScrollLeft) {
        return;
      }

      var drawer = document.querySelector(DRAWER);
      var list = drawer && drawer.querySelector(ADDON_LIST);

      if (list) {
        list.scrollLeft = listScrollLeft;
      }
    },
    true
  );

})();
